<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isExtensionAllowed } from 'shared/helpers/FileHelper';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';

const ACCEPTED_FILE_TYPES =
  'image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.json,.xml';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    EmojiInput,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachedFiles: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
      showFormattingBar: true,
      showEmojiPicker: false,
      updateEditorSelectionWith: '',
    };
  },
  computed: {
    acceptedFileTypes() {
      return ACCEPTED_FILE_TYPES;
    },
    isFormValid() {
      const hasContent = !this.v$.content.$invalid;
      const hasShortCode = !this.v$.shortCode.$invalid;
      const hasAttachments = this.attachedFiles.length > 0;
      return hasShortCode && (hasContent || hasAttachments);
    },
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachedFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onFileChange(event) {
      const files = Array.from(event.target.files);
      const invalidFiles = [];
      files.forEach(file => {
        if (!isExtensionAllowed(file.name)) {
          invalidFiles.push(file.name);
          return;
        }
        this.attachedFiles.push({
          file,
          name: file.name,
          type: file.type,
          size: file.size,
          preview: file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : null,
        });
      });
      if (invalidFiles.length) {
        useAlert(this.$t('CONVERSATION.FILE_TYPE_INVALID'));
      }
      event.target.value = '';
    },
    removeFile(index) {
      const removed = this.attachedFiles.splice(index, 1);
      if (removed[0]?.preview) {
        URL.revokeObjectURL(removed[0].preview);
      }
    },
    viewAttachment(attachment) {
      if (attachment.file) {
        const fileUrl =
          attachment.preview || URL.createObjectURL(attachment.file);
        window.open(fileUrl, '_blank');
      }
    },
    getFileIcon(type) {
      if (type.startsWith('image/')) return 'i-ph-image';
      if (type.startsWith('audio/')) return 'i-ph-file-audio';
      if (type.startsWith('video/')) return 'i-ph-file-video';
      return 'i-ph-file';
    },
    toggleFormattingBar() {
      this.showFormattingBar = !this.showFormattingBar;
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      this.showEmojiPicker = false;
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.hideEmojiPicker();
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    buildFormData() {
      const formData = new FormData();
      formData.append('canned_response[short_code]', this.shortCode);
      formData.append('canned_response[content]', this.content);
      this.attachedFiles.forEach(({ file }) => {
        formData.append('canned_response[attachments][]', file);
      });
      return formData;
    },
    addCannedResponse() {
      this.addCanned.showLoading = true;
      const payload = this.attachedFiles.length
        ? this.buildFormData()
        : { short_code: this.shortCode, content: this.content };

      this.$store
        .dispatch('createCannedResponse', payload)
        .then(() => {
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor popover-prosemirror-menu [&>div]:px-1"
              :class="{
                editor_warning: v$.content.$error,
                'show-formatting-bar': showFormattingBar,
              }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              show-source-toggle
              override-line-breaks
              :update-selection-with="updateEditorSelectionWith"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
              @clear-selection="clearEditorSelection"
            />
            <div class="relative flex items-center gap-2 px-3 py-3">
              <EmojiInput
                v-if="showEmojiPicker"
                v-on-clickaway="hideEmojiPicker"
                class="absolute bottom-full left-0 z-[60] mb-2"
                :on-click="addIntoEditor"
              />
              <NextButton
                v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
                type="button"
                icon="i-ph-text-aa"
                :variant="showFormattingBar ? 'solid' : 'faded'"
                color="slate"
                sm
                :aria-pressed="showFormattingBar"
                @click="toggleFormattingBar"
              />
              <NextButton
                v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
                type="button"
                icon="i-ph-smiley-sticker"
                slate
                faded
                sm
                @click="toggleEmojiPicker"
              />
            </div>
          </div>
        </div>

        <!-- Attachment section -->
        <div class="w-full mb-4">
          <label
            class="flex items-center gap-2 mb-2 text-sm font-medium text-n-slate-11"
          >
            {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.LABEL') }}
          </label>
          <label
            class="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-n-weak hover:border-n-slate-7 cursor-pointer transition-colors text-sm text-n-slate-10 hover:text-n-slate-12"
          >
            <i class="i-ph-paperclip text-base" />
            <span>{{
              $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.PLACEHOLDER')
            }}</span>
            <input
              type="file"
              multiple
              :accept="acceptedFileTypes"
              class="hidden"
              @change="onFileChange"
            />
          </label>

          <!-- File previews -->
          <div v-if="attachedFiles.length" class="flex flex-wrap gap-2 mt-3">
            <div
              v-for="(attachment, index) in attachedFiles"
              :key="index"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak text-sm"
            >
              <img
                v-if="attachment.preview"
                :src="attachment.preview"
                class="size-8 rounded object-cover cursor-pointer"
                :title="$t('CANNED_MGMT.ATTACHMENTS.PREVIEW')"
                @click="viewAttachment(attachment)"
              />
              <i v-else class="text-lg" :class="getFileIcon(attachment.type)" />
              <div class="flex flex-col min-w-0">
                <span class="truncate max-w-[150px] text-n-slate-12">
                  {{ attachment.name }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ formatFileSize(attachment.size) }}
                </span>
              </div>
              <div class="flex items-center gap-1.5 ml-2">
                <button
                  type="button"
                  class="text-n-slate-10 hover:text-n-slate-12 transition-colors cursor-pointer"
                  :title="$t('CANNED_MGMT.ATTACHMENTS.PREVIEW')"
                  @click="viewAttachment(attachment)"
                >
                  <i class="i-ph-eye text-base" />
                </button>
                <button
                  type="button"
                  class="text-n-slate-10 hover:text-n-ruby-9 transition-colors cursor-pointer"
                  :title="$t('CANNED_MGMT.ATTACHMENTS.DELETE')"
                  @click="removeFile(index)"
                >
                  <i class="i-ph-x text-base" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            icon="i-lucide-x"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            icon="i-lucide-send"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="!isFormValid || addCanned.showLoading"
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
